import type { FC } from "react"
import { useTranslation } from "@warpme/i18n"
import { matchPath, useLocation } from "react-router"
import { Helmet } from "react-helmet-async"

interface Props {
  title?: string
  description?: string
}

const TITLE_PREFIX = "WarpMe"

const resolveDefaultTitle = (
  pathname: string,
  t: (key: string) => string,
  tAuth: (key: string) => string,
  tPurchase: (key: string) => string
) => {
  const routeTitles = [
    { path: "/", title: t("browseTickets.title") },
    { path: "/login", title: tAuth("login") },
    { path: "/signup", title: tAuth("accountRegistration") },
    { path: "/login/forget-password", title: t("resetPassword.title") },
    { path: "/login/forget-password/sent", title: t("resetPassword.sent") },
    { path: "/login/reset-password", title: t("resetPassword.title") },
    { path: "/not-found", title: t("notFound.title") },
    { path: "/restaurants/:restaurantId", title: t("meta.restaurantTitle") },
    { path: "/restaurants/:restaurantId/tickets/:ticketId", title: t("meta.ticketTitle") },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/purchase",
      title: tPurchase("title"),
    },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/purchase/card",
      title: tPurchase("cardRegistrationTitle"),
    },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/purchase/return",
      title: t("meta.purchaseResultTitle"),
    },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/guest-purchase",
      title: t("meta.guestPurchaseTitle"),
    },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/guest-purchase/card",
      title: tPurchase("cardRegistrationTitle"),
    },
    {
      path: "/restaurants/:restaurantId/tickets/:ticketId/guest-purchase/return",
      title: t("meta.guestPurchaseResultTitle"),
    },
    { path: "/ticket", title: t("meta.guestTicketTitle") },
    { path: "/purchases", title: t("purchaseHistory.title") },
    { path: "/purchases/:id", title: t("meta.purchaseDetailTitle") },
    { path: "/purchases/:id/use", title: t("meta.purchaseUseTitle") },
    { path: "/account", title: t("account.title") },
    { path: "/account/mail", title: t("updateEmail.title") },
    { path: "/account/password", title: t("updatePassword.title") },
    { path: "/account/card", title: t("cards.title") },
  ]

  return routeTitles.find(({ path }) => matchPath({ path, end: true }, pathname))?.title
}

export const MetaTags: FC<Props> = ({ title, description }) => {
  const { pathname } = useLocation()
  const { t } = useTranslation("consumer")
  const { t: tAuth } = useTranslation("auth")
  const { t: tPurchase } = useTranslation("purchase")
  const resolvedTitle =
    title ?? resolveDefaultTitle(pathname, t, tAuth, tPurchase) ?? t("meta.defaultTitle")
  const resolvedDescription =
    description ??
    (pathname === "/not-found" ? t("notFound.description") : t("meta.defaultDescription"))

  return (
    <Helmet>
      <title>
        {resolvedTitle === TITLE_PREFIX ? TITLE_PREFIX : `${TITLE_PREFIX} | ${resolvedTitle}`}
      </title>
      <meta name="description" content={resolvedDescription} />
      <meta property="og:site_name" content={TITLE_PREFIX} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
    </Helmet>
  )
}
